import { useCallback, useEffect, useMemo, useReducer, type ReactElement } from 'react'
import type { FeedbackAnalyticsReporter, FeedbackContext } from './types'
import { buildConfiguration } from './config'
import {
  canSubmit as canSubmitState,
  feedbackReducer,
  initialFeedbackState,
  submitPayload,
} from './store'
import { FeedbackStrings, type FeedbackStringSet } from './strings'
import { FeedbackHeaderIcon, FeedbackFinishIcon } from './icons'
import styles from './FeedbackView.module.css'

/** How long the ThankYou screen stays before auto-dismiss (mirrors iOS 2s). */
export const THANK_YOU_DISMISS_MS = 2000

export interface FeedbackViewProps {
  /** Which surface opened the form — selects the configuration. */
  readonly context: FeedbackContext
  /**
   * Presentation chrome (H contract). `overlay` (default) = phone
   * full-screen backdrop + bottom sheet, backdrop-click + Escape dismiss.
   * `sidePanel` = tablet/desktop centered modal / form-sheet, Escape
   * dismiss only. Mirrors the RouteDetails overlay split.
   */
  readonly layout?: 'overlay' | 'sidePanel'
  /** Dismiss the form (backdrop / Escape / post-ThankYou auto-dismiss). */
  readonly onClose: () => void
  /** Analytics sink — host forwards to its reporter. Defaults to no-op. */
  readonly onAnalytics?: FeedbackAnalyticsReporter
  /**
   * Test seam: a 2s timer drives the ThankYou → dismiss transition. Tests
   * pass `0` to dismiss synchronously (or fake timers).
   */
  readonly thankYouDismissMs?: number
  /**
   * Localized copy. Defaults to the package's English set so the module
   * renders standalone; the host builds a localized one from the `feedback`
   * i18next namespace (`src/i18n/packageStrings.ts`).
   */
  readonly strings?: FeedbackStringSet
}

const noopAnalytics: FeedbackAnalyticsReporter = () => {}

export function FeedbackView({
  context,
  layout = 'overlay',
  onClose,
  onAnalytics = noopAnalytics,
  thankYouDismissMs = THANK_YOU_DISMISS_MS,
  strings = FeedbackStrings,
}: FeedbackViewProps): ReactElement {
  const config = useMemo(() => buildConfiguration(context, strings), [context, strings])
  const [state, dispatch] = useReducer(feedbackReducer, config, initialFeedbackState)
  const isSidePanel = layout === 'sidePanel'
  const canSubmit = canSubmitState(state)

  // Every way OUT of the form that is not a submit: the "Not now" button,
  // Escape, and the backdrop. Reported as `feedback_dismissed` — a form
  // people bail out of is a product signal, and `feedback_submitted` alone
  // cannot tell whether 55 submissions came out of 60 opens or 600. The
  // thank-you auto-dismiss is NOT a bail-out and never routes through here.
  const dismiss = useCallback((): void => {
    onAnalytics('feedback_dismissed', {
      reasons_selected: state.selectedReasonIds.size,
      has_note: state.note.trim().length > 0,
    })
    onClose()
  }, [state.selectedReasonIds, state.note, onAnalytics, onClose])

  // Escape dismiss (both layouts) — mirrors the RouteDetails overlay.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return (): void => window.removeEventListener('keydown', onKeyDown)
  }, [dismiss])

  // After a successful submit the ThankYou screen shows, then the form
  // auto-dismisses after ~2s (mirrors iOS `submitSucceeded` → finished →
  // sleep → dismiss). The timer is keyed on `showingThankYou`.
  useEffect(() => {
    if (!state.showingThankYou) return
    const id = window.setTimeout(onClose, thankYouDismissMs)
    return (): void => window.clearTimeout(id)
  }, [state.showingThankYou, thankYouDismissMs, onClose])

  const onSubmit = useCallback(() => {
    if (!canSubmitState(state)) return
    onAnalytics('feedback_submitted', submitPayload(state))
    dispatch({ kind: 'Submitted' })
  }, [state, onAnalytics])

  const onBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      // Backdrop dismiss is overlay-only; the sidePanel is a centered
      // modal whose backdrop also closes (matches the RouteDetails
      // modal feel), but the inner card stops propagation.
      if (e.target === e.currentTarget) dismiss()
    },
    [dismiss],
  )

  const body = state.showingThankYou ? (
    <ThankYou strings={strings} />
  ) : (
    <>
      <div className={styles.scroll}>
        <div className={styles.stack}>
          <header className={styles.header}>
            <FeedbackHeaderIcon name={config.iconName} />
            <h2 className={styles.headerTitle}>{config.title}</h2>
          </header>
          <div className={styles.categories}>
            {config.categories.map((category) => (
              <section key={category.id} className={styles.category}>
                <div className={styles.categoryTitle}>{category.title}</div>
                <div className={styles.flow}>
                  {category.reasons.map((reason) => {
                    const selected = state.selectedReasonIds.has(reason.id)
                    return (
                      <button
                        key={reason.id}
                        type="button"
                        className={selected ? `${styles.chip} ${styles.chipSelected}` : styles.chip}
                        aria-pressed={selected}
                        data-testid={`feedback-reason-${reason.id}`}
                        onClick={() => dispatch({ kind: 'ReasonToggled', reasonId: reason.id })}
                      >
                        {reason.title}
                      </button>
                    )
                  })}
                </div>
              </section>
            ))}
            <textarea
              className={styles.note}
              placeholder={strings.notePlaceholder}
              value={state.note}
              data-testid="feedback-note"
              onChange={(e) => dispatch({ kind: 'NoteChanged', note: e.target.value })}
            />
          </div>
        </div>
      </div>
      <div className={styles.bottomBar}>
        <button
          type="button"
          className={styles.submit}
          disabled={!canSubmit}
          data-testid="feedback-submit"
          onClick={onSubmit}
        >
          {config.submitTitle}
        </button>
        {/*
          The way out. Submit stays disabled until a reason or a note exists,
          so without this a rider who opened the form by accident is left
          hunting for the Escape key. Secondary styling (plain text under the
          primary CTA) mirrors iOS `FeedbackBottomBar` and Android `BottomBar`.
        */}
        <button
          type="button"
          className={styles.notNow}
          data-testid="feedback-not-now"
          onClick={dismiss}
        >
          {strings.notNow}
        </button>
        {config.footerText !== undefined && (
          <div className={styles.footer}>{config.footerText}</div>
        )}
      </div>
    </>
  )

  // a11y root id mirrors iOS `feedback-view-root` (cross-platform UI-test
  // contract — the Report-an-issue tap opens this view).
  const dialogProps = {
    'data-testid': 'feedback-view-root',
    role: 'dialog' as const,
    'aria-modal': true as const,
    'aria-label': 'Feedback',
  }

  if (isSidePanel) {
    return (
      <div className={styles.centeredBackdrop} onClick={onBackdropClick}>
        <div className={styles.card} {...dialogProps}>
          {body}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.backdrop} onClick={onBackdropClick}>
      <div className={styles.sheet} {...dialogProps}>
        {body}
      </div>
    </div>
  )
}

/** ThankYou success screen — finish badge + title + description. */
function ThankYou({ strings }: { strings: FeedbackStringSet }): ReactElement {
  return (
    <div className={styles.thankYou} data-testid="feedback-thank-you">
      <FeedbackFinishIcon />
      <div>
        <h2 className={styles.thankYouTitle}>{strings.thankYouTitle}</h2>
        <p className={styles.thankYouDescription}>{strings.thankYouDescription}</p>
      </div>
    </div>
  )
}
